import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  ArrowLeft, 
  CheckCircle, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Building2, 
  Loader2,
  ShieldCheck
} from 'lucide-react';

import Header from '../../Components/Header';
import Footer from '../../Components/Footer';

const Book = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const product = location.state?.product;

  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerAddress: '',
    organisation: ''
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Redirect if no product data is found
  useEffect(() => {
    document.title = "Marvel Sato | Book";

    if (!product) {
      navigate('/products');
    }
  }, [product, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const bookingPayload = {
      productId: product._id,
      ...formData
    };

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/bookings/product/book`, bookingPayload);
      if (response.status === 201) {
        setSuccess(true);
        setTimeout(() => navigate('/products'), 3000);
      }
    } catch (error) {
      console.error("Booking failed:", error.response?.data || error.message);
      alert("Failed to create booking. Please check your details.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="h-screen flex items-center justify-center bg-white">
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }} 
          animate={{ scale: 1, opacity: 1 }}
          className="text-center"
        >
          <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={48} />
          </div>
          <h2 className="text-3xl font-black uppercase italic tracking-tighter mb-2">Booking Confirmed!</h2>
          <p className="text-slate-500 font-medium">Our team will contact you shortly regarding the {product?.productName}.</p>
          <p className="text-orange-500 text-sm mt-4 animate-pulse font-bold uppercase">Redirecting to fleet...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen font-sans">
      <Header />
      
      <div className="max-w-7xl mx-auto px-6 pt-32 pb-20">
        <Link to="/products" className="inline-flex items-center gap-2 text-slate-400 hover:text-orange-500 font-bold text-xs uppercase tracking-widest mb-8 transition-colors">
          <ArrowLeft size={16} /> Back to Products
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* LEFT: PRODUCT SUMMARY */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-white p-6 rounded-3xl shadow-xl shadow-slate-200/60 border border-slate-100">
              <img 
                src={product?.productImage1} 
                alt={product?.productName} 
                className="w-full h-64 object-cover rounded-2xl mb-6"
              />
              <h2 className="text-3xl font-black text-slate-900 uppercase italic tracking-tighter mb-2">
                {product?.productName}
              </h2>
              <div className="flex items-center gap-2 text-orange-500 font-black text-xs uppercase tracking-widest mb-6">
                <ShieldCheck size={16} /> Premium Industrial Series
              </div>

              <div className="space-y-4 border-t border-slate-50 pt-6">
                <div className="flex justify-between">
                  <span className="text-slate-400 text-xs font-bold uppercase">Lifting Capacity</span>
                  <span className="text-slate-900 font-black">{product?.productCapacity}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 text-xs font-bold uppercase">Boom Length</span>
                  <span className="text-slate-900 font-black">{product?.productBoomLength}</span>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: BOOKING FORM */}
          <div className="lg:col-span-7">
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white p-8 md:p-12 rounded-3xl shadow-2xl shadow-slate-200/50 border border-slate-100"
            >
              <h3 className="text-2xl font-black text-slate-900 uppercase italic tracking-tighter mb-8">
                Confirm your <span className="text-orange-500">Booking</span>
              </h3>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Full Name</label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
                      <input 
                        required
                        name="customerName"
                        value={formData.customerName}
                        onChange={handleChange}
                        className="w-full bg-slate-50 border-2 border-slate-50 rounded-xl py-4 pl-12 pr-4 focus:border-orange-500 focus:bg-white outline-none transition-all font-medium"
                        placeholder="Name"
                      />
                    </div>
                  </div>

                  {/* Organisation */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Organisation</label>
                    <div className="relative">
                      <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
                      <input 
                        required
                        name="organisation"
                        value={formData.organisation}
                        onChange={handleChange}
                        className="w-full bg-slate-50 border-2 border-slate-50 rounded-xl py-4 pl-12 pr-4 focus:border-orange-500 focus:bg-white outline-none transition-all font-medium"
                        placeholder="Company Name"
                      />
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Work Email</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
                    <input 
                      required
                      type="email"
                      name="customerEmail"
                      value={formData.customerEmail}
                      onChange={handleChange}
                      className="w-full bg-slate-50 border-2 border-slate-50 rounded-xl py-4 pl-12 pr-4 focus:border-orange-500 focus:bg-white outline-none transition-all font-medium"
                      placeholder="name@company.com"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
                    <input 
                      required
                      type="tel"
                      name="customerPhone"
                      value={formData.customerPhone}
                      onChange={handleChange}
                      className="w-full bg-slate-50 border-2 border-slate-50 rounded-xl py-4 pl-12 pr-4 focus:border-orange-500 focus:bg-white outline-none transition-all font-medium"
                      placeholder="+91 0000-000000"
                    />
                  </div>
                </div>

                {/* Address */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Site Address</label>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-4 text-slate-300" size={18} />
                    <textarea 
                      required
                      name="customerAddress"
                      value={formData.customerAddress}
                      onChange={handleChange}
                      rows="3"
                      className="w-full bg-slate-50 border-2 border-slate-50 rounded-xl py-4 pl-12 pr-4 focus:border-orange-500 focus:bg-white outline-none transition-all font-medium"
                      placeholder="Enter Site address"
                    />
                  </div>
                </div>

                <button 
                  disabled={loading}
                  className="w-full bg-slate-900 text-white py-5 rounded-xl font-black uppercase tracking-widest hover:bg-orange-500 transition-all shadow-xl shadow-slate-900/10 flex items-center justify-center gap-3 disabled:opacity-70"
                >
                  {loading ? (
                    <>Processing <Loader2 className="animate-spin" size={20} /></>
                  ) : (
                    "Confirm Booking"
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Book;